import React, { useEffect, useRef } from 'react';
import { Box, Typography, Stepper, Step, StepLabel, StepContent, Chip } from '@mui/material';
import { getColor, getDisplayName } from '../../../../../../constants/skillLevels';

const StepNavigation = ({
  activeStep,
  setActiveStep,
  myReviewer,
  isDirty,
  isStepComplete,
  handleStep,
  startAssignmentMessage,
  getSegmentLevel,
  getOverallLevel,
  defaultLevelId,
  skillLevels,
  shouldShowActionsStep
}) => {
  const activeStepRef = useRef(null);

  // Scroll active step into view when it changes
  useEffect(() => {
    if (activeStepRef.current) {
      activeStepRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [activeStep]);

  return (
    <>
      <Stepper nonLinear orientation="vertical" activeStep={activeStep}>
        {[
          !myReviewer?.startedAt && (
            <Step
              key="start"
              active={activeStep === 0}
              completed={activeStep > 0}
            >
              <StepLabel
                onClick={() => !isDirty && setActiveStep(0)}
                sx={{
                  cursor: isDirty ? 'not-allowed' : 'pointer',
                  opacity: 1
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                  Start Assignment
                </Typography>
              </StepLabel>
              <StepContent slotProps={{ transition: { unmountOnExit: false } }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Welcome to Skills Matrix Assessment
                  </Typography>
                  <Typography variant="body1">
                    {startAssignmentMessage}
                  </Typography>
                </Box>
              </StepContent>
            </Step>
          ),
          ...myReviewer?.skillsMatrixTemplate?.skillsTemplates?.map((skillTemplate, index) => (
            <Step
              key={skillTemplate.id}
              ref={activeStep === index + 1 ? activeStepRef : null}
              active={activeStep === index + 1}
              completed={isStepComplete(index + 1)}
            >
              <StepLabel
                onClick={handleStep(index + 1, isDirty)}
                sx={{
                  cursor: isDirty || activeStep === 0 ? 'not-allowed' : 'pointer',
                  opacity: activeStep === 0 ? 0.5 : 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                    {skillTemplate.skill.name}
                  </Typography>
                  {skillTemplate.isRequired === false && (
                    <Chip
                      label="Optional"
                      size="small"
                      sx={{
                        height: '20px',
                        fontSize: '10px',
                        backgroundColor: '#E0E0E0',
                        color: '#666'
                      }}
                    />
                  )}
                </Box>
              </StepLabel>
              <StepContent slotProps={{ transition: { unmountOnExit: false } }}>
                <Box sx={{ mb: 2 }}>
                  {skillTemplate.mappings?.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Assessed Skills
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px', ml: 2 }}>
                        {skillTemplate.mappings?.filter(mapping => getSegmentLevel(index, mapping.segment.id) !== defaultLevelId).length || 0}
                        {' / '}
                        {skillTemplate.mappings?.length || 0} responses
                      </Typography>
                    </>
                  )}
                  {!(skillTemplate.mappings?.length > 0) && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1, mt: skillTemplate.mappings?.length > 0 ? 2 : 0, color: 'text.secondary' }}>
                        Overall Competency
                      </Typography>
                      <Box sx={{ 
                        ml: 2,
                        display: 'flex',
                        alignItems: 'center',
                        opacity: isDirty ? 0.6 : 1,
                        backgroundColor: getColor(getOverallLevel(index) || defaultLevelId, skillLevels),
                        p: '6px 12px',
                        borderRadius: '4px',
                        width: '140px'
                      }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            marginRight: 1,
                            backgroundColor: getColor(getOverallLevel(index) || defaultLevelId, skillLevels),
                            border: '1px solid #333'
                          }}
                        />
                        <Typography sx={{ 
                          color: '#000',
                          fontSize: '12px'
                        }}>
                          {getDisplayName(getOverallLevel(index) || defaultLevelId, skillLevels)}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </StepContent>
            </Step>
          )),
          // Visual separator before summary section
          <Box key="separator" sx={{ 
            my: 3, 
            mx: 2,
            display: 'flex',
            alignItems: 'center',
            opacity: 0.6
          }}>
            <Box sx={{ 
              flex: 1, 
              height: '1px', 
              backgroundColor: 'divider' 
            }} />
            <Typography variant="caption" sx={{ 
              mx: 2, 
              color: 'text.secondary',
              fontSize: '12px',
              fontWeight: 500,
              letterSpacing: '0.5px'
            }}>
              End of Assessment
            </Typography>
            <Box sx={{ 
              flex: 1, 
              height: '1px', 
              backgroundColor: 'divider' 
            }} />
          </Box>,
          <Step
            key="summary"
            ref={activeStep === myReviewer?.skillsMatrixTemplate?.skillsTemplates?.length + 1 ? activeStepRef : null}
            active={activeStep === myReviewer?.skillsMatrixTemplate?.skillsTemplates?.length + 1}
            completed={isStepComplete(myReviewer?.skillsMatrixTemplate?.skillsTemplates?.length + 1)}
          >
            <StepLabel
              onClick={handleStep(myReviewer?.skillsMatrixTemplate?.skillsTemplates?.length + 1, isDirty)}
              sx={{
                  cursor: isDirty ? 'not-allowed' : 'pointer',
                  opacity: 1
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                SUMMARY
              </Typography>
            </StepLabel>
            <StepContent slotProps={{ transition: { unmountOnExit: false } }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Skills Overview
                </Typography>
              </Box>
            </StepContent>
          </Step>,
          shouldShowActionsStep && (
            <Step
              key="actions"
              ref={activeStep === myReviewer?.skillsMatrixTemplate?.skillsTemplates?.length + 2 ? activeStepRef : null}
              active={activeStep === myReviewer?.skillsMatrixTemplate?.skillsTemplates?.length + 2}
              completed={isStepComplete(myReviewer?.skillsMatrixTemplate?.skillsTemplates?.length + 2)}
            >
              <StepLabel
                onClick={handleStep(myReviewer?.skillsMatrixTemplate?.skillsTemplates?.length + 2, isDirty)}
                sx={{
                    cursor: isDirty ? 'not-allowed' : 'pointer',
                    opacity: 1
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                FEEDBACK ACTIONS
                </Typography>
              </StepLabel>
              <StepContent slotProps={{ transition: { unmountOnExit: false } }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    START / STOP / CONTINUE
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                    Provide actionable feedback
                  </Typography>
                </Box>
              </StepContent>
            </Step>
          )
        ]}
      </Stepper>
      {!myReviewer?.skillsMatrixTemplate?.skillsTemplates && (
        <Typography color="text.secondary">No skills templates available</Typography>
      )}
    </>
  );
};

export default StepNavigation; 